import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import * as fromGenerated from '../../../_generated';
import {
  AssistantActions,
  AssistantBuilderModalActions,
  AssistantsActions,
} from './assistants.actions';
import { sortAssistantsComparer } from './assistants.store.helper';

export const assistantsFeatureKey = 'assistants';

/**
 * Skill-builder modal state (SB-3325 PR-4, Stage B). `closed` hides the modal;
 * `new` opens it for creation; `edit` opens it for `assistant` — the full
 * `AssistantView` snapshot captured at click time.
 *
 * The snapshot is the fix for the live-binding regression: the builder patches
 * its forms off its `[assistant]` input, so the edit target must be a stable
 * reference for the modal's lifetime. A background `loadAssistants` reload uses
 * `setAll` (new entity refs) and never touches `builderModal`, so a snapshot
 * kept here survives such reloads — no re-patch, no reset, no share re-seed, and
 * it stays defined even if the skill leaves the accessible set (which would
 * otherwise drop the entity and push Save onto the CREATE branch → duplicate).
 * `assistantUuid` is kept alongside for convenience (e.g. delete targeting).
 */
export interface AssistantBuilderModalState {
  kind: 'closed' | 'new' | 'edit';
  assistantUuid: string | null;
  assistant: fromGenerated.AssistantView | null;
}

export interface AssistantsState extends EntityState<fromGenerated.AssistantView> {
  loaded: boolean;
  error: string | null;
  builderModal: AssistantBuilderModalState;
}

const entityAdapter: EntityAdapter<fromGenerated.AssistantView> =
  createEntityAdapter<fromGenerated.AssistantView>({
    selectId: (view) => view.uuid,
    sortComparer: sortAssistantsComparer,
  });

export const initialAssistantsState: AssistantsState =
  entityAdapter.getInitialState({
    loaded: false,
    error: null,
    builderModal: { kind: 'closed', assistantUuid: null, assistant: null },
  });

export const assistantsFeature = createFeature({
  name: assistantsFeatureKey,
  reducer: createReducer(
    initialAssistantsState,
    on(AssistantsActions.loadAssistantsSuccess, (state, { assistants }) =>
      entityAdapter.setAll(assistants, { ...state, error: null, loaded: true }),
    ),
    on(
      AssistantActions.receivedAssistantStatusUpdatedFromSocket,
      (state, { uuid, status, documents }) =>
        entityAdapter.updateOne(
          { id: uuid, changes: { status, documents } },
          { ...state },
        ),
    ),
    on(AssistantActions.duplicateAssistant, (state) => ({
      ...state,
      error: null,
    })),
    on(AssistantActions.duplicateAssistantSuccess, (state, { assistant }) =>
      entityAdapter.upsertOne(assistant, { ...state, error: null }),
    ),
    on(AssistantActions.duplicateAssistantFailure, (state, { error }) => ({
      ...state,
      error,
    })),
    on(AssistantActions.deleteAssistant, (state) => ({
      ...state,
      error: null,
    })),
    on(AssistantActions.deleteAssistantSuccess, (state, { uuid }) =>
      entityAdapter.removeOne(uuid, { ...state, error: null }),
    ),
    on(AssistantActions.deleteAssistantFailure, (state, { error }) => ({
      ...state,
      error,
    })),
    on(AssistantBuilderModalActions.openCreate, (state) => ({
      ...state,
      builderModal: {
        kind: 'new' as const,
        assistantUuid: null,
        assistant: null,
      },
    })),
    on(AssistantBuilderModalActions.openEdit, (state, { assistant }) => ({
      ...state,
      builderModal: {
        kind: 'edit' as const,
        assistantUuid: assistant.uuid,
        assistant,
      },
    })),
    on(AssistantBuilderModalActions.close, (state) => ({
      ...state,
      builderModal: {
        kind: 'closed' as const,
        assistantUuid: null,
        assistant: null,
      },
    })),
  ),
  extraSelectors: ({ selectAssistantsState }) => {
    const entitySelectors = entityAdapter.getSelectors(selectAssistantsState);

    return {
      ...entitySelectors,
    };
  },
});
